import { MAX_UPLOAD_DIMENSION, UPLOAD_JPEG_QUALITY } from './resizeImageForUpload.constants';

/**
 * Downscales a photo to the upload size (JPEG, long edge 2048 px) when it is larger.
 * Uses the browser's native decoder, which also applies the EXIF orientation. Returns the
 * original untouched when it is already small enough or can't be decoded (e.g. HEIC in
 * Chrome), so callers compare identity to tell.
 * @param image Web File (URIs are returned untouched)
 * @param width Source width when known, to skip decoding photos that are already small
 * @param height Source height when known
 */
export async function resizeImageForUpload<T extends string | File>(
  image: T,
  width?: number,
  height?: number
): Promise<T> {
  if (typeof image === 'string') return image;
  if (width && height && Math.max(width, height) <= MAX_UPLOAD_DIMENSION) return image;

  const file = image as File;
  let bitmap: ImageBitmap | undefined;
  try {
    bitmap = await createImageBitmap(file);
    const scale = MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height);
    if (scale >= 1) return image;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    if (!context) return image;
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', UPLOAD_JPEG_QUALITY)
    );
    if (!blob) return image;

    const name = file.name.replace(/\.[^.]*$/, '') || 'photo';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' }) as T;
  } catch (error) {
    console.warn('Image resize failed, uploading the original:', error);
    return image;
  } finally {
    bitmap?.close();
  }
}
